import { useMemo } from 'react';
import { t } from '@aquabox/shared';
import {
  useAbantEmployeeNames,
  useHayatVehicleLookup,
  useMe,
  usePanelVehicleLookup,
} from '../api/queries';
import { usePanelAssign, type PanelAssignLabel } from '../api/panel-assign';

/**
 * "Araç / kişi etiketi": panelin ham araç etiketini (Abant çalışan no, Hayat "ALT/ÜST BÖLGE")
 * okunur tek satıra çevirir — "Plaka · Personel adı". Çıplak numara HİÇBİR ekranda gösterilmez.
 *
 * Eşleme DİNAMİKTİR: kaynak `GET /api/panel-assign` (Abant `driverMap` → şoför, bugünkü araç) ve
 * kayıtlı `vehicleMap`/`employeeNames`. Ad/numara koda yazılmaz; eşleme değişince ekran değişir.
 */

export interface PanelLabelInfo {
  plate: string | null;
  person: string | null;
  /** Etiket bizim şoför/araca bağlı mı (Panel Atama'da eşlenmiş). */
  mapped: boolean;
}

export type PanelLabelLookup = (
  source: string,
  raw: string | null | undefined,
) => PanelLabelInfo | null;

export type VehicleLabelKind = 'ours' | 'mapped' | 'region' | 'unmapped' | 'none';

export interface VehicleLabel {
  kind: VehicleLabelKind;
  /** Tek satır metin; `none` için "—". */
  text: string;
  /** Eşleme yok ipucu gösterilsin mi ("Panel Atama'dan eşle"). */
  needsMapping: boolean;
}

/** "Plaka · Ad" — yalnız bilinen parçalar birleştirilir. */
export function plateAndPerson(plate: string | null, person: string | null): string {
  return [plate, person].filter((p): p is string => !!p).join(' · ');
}

/** "ALT BÖLGE" → "Bölge: Alt". Bölge etiketi değilse null. */
export function regionText(raw: string): string | null {
  const m = /^(.*?)\s*B[ÖO]LGE\s*$/iu.exec(raw.trim().toLocaleUpperCase('tr-TR'));
  if (!m || !m[1]) return null;
  const name = m[1].trim().toLocaleLowerCase('tr-TR');
  const title = name.charAt(0).toLocaleUpperCase('tr-TR') + name.slice(1);
  return t('orders.vehicleLabel.region', { name: title });
}

interface OrderVehicleInput {
  source: string;
  vehicle?: { plate?: string | null } | null;
  externalVehicleRaw?: string | null;
}

const isPanel = (source: string) => source === 'HAYAT_PANEL' || source === 'ABANT_PANEL';

export function resolveVehicleLabel(
  order: OrderVehicleInput,
  lookup: PanelLabelLookup,
): VehicleLabel {
  const raw = order.externalVehicleRaw?.trim() || null;
  const info = raw && isPanel(order.source) ? lookup(order.source, raw) : null;
  const ourPlate = order.vehicle?.plate ?? null;

  if (ourPlate) {
    // Aynı araca eşleşen panel kişisi varsa ad eklenir; farklı araçsa panel etiketi karıştırılmaz.
    const person = info && (!info.plate || info.plate === ourPlate) ? info.person : null;
    return { kind: 'ours', text: plateAndPerson(ourPlate, person), needsMapping: false };
  }
  if (!raw) return { kind: 'none', text: '—', needsMapping: false };

  if (info && (info.plate || info.person)) {
    return {
      kind: 'mapped',
      text: plateAndPerson(info.plate, info.person),
      needsMapping: !info.mapped,
    };
  }
  const region = regionText(raw);
  if (region) return { kind: 'region', text: region, needsMapping: isPanel(order.source) };
  // Eşlemesi olmayan çalışan numarası ya da bilinmeyen etiket: numara gösterilmez.
  return { kind: 'unmapped', text: t('orders.vehicleLabel.unmapped'), needsMapping: true };
}

function fromLabel(l: PanelAssignLabel | undefined): PanelLabelInfo | null {
  if (!l) return null;
  const plate = l.target?.plate ?? null;
  const person = l.driver?.name ?? l.displayName ?? null;
  if (!plate && !person) return null;
  return { plate, person, mapped: !!l.driver || !!plate };
}

/**
 * Siparişler + sipariş detayı için tek arayış. Önce Panel Atama (driverMap/bugünkü araç), yoksa
 * kayıtlı vehicleMap (+ Abant çalışan adı). Sorgular yalnız OWNER/MANAGER için açılır.
 */
export function usePanelLabelLookup(): PanelLabelLookup {
  const me = useMe();
  const allowed = me.data?.role === 'OWNER' || me.data?.role === 'MANAGER';
  const abantAssign = usePanelAssign(allowed ? 'ABANT_PANEL' : null);
  const hayatAssign = usePanelAssign(allowed ? 'HAYAT_PANEL' : null);
  const hayatVehicle = useHayatVehicleLookup();
  const abantVehicle = usePanelVehicleLookup('ABANT_PANEL');
  const abantNames = useAbantEmployeeNames();
  const abantLabels = abantAssign.data?.labels;
  const hayatLabels = hayatAssign.data?.labels;

  return useMemo<PanelLabelLookup>(
    () => (source, raw) => {
      if (!raw) return null;
      if (source === 'ABANT_PANEL') {
        const a = fromLabel(
          Array.isArray(abantLabels) ? abantLabels.find((l) => l.label === raw) : undefined,
        );
        const plate = a?.plate ?? abantVehicle(raw)?.plate ?? null;
        const person = a?.person ?? abantNames[raw] ?? null;
        if (!plate && !person) return null;
        return { plate, person, mapped: a?.mapped ?? !!plate };
      }
      if (source === 'HAYAT_PANEL') {
        const h = fromLabel(
          Array.isArray(hayatLabels) ? hayatLabels.find((l) => l.label === raw) : undefined,
        );
        const plate = h?.plate ?? hayatVehicle(raw)?.plate ?? null;
        return plate ? { plate, person: h?.person ?? null, mapped: true } : null;
      }
      return null;
    },
    [abantLabels, hayatLabels, abantVehicle, hayatVehicle, abantNames],
  );
}
